import { Controller } from "@hotwired/stimulus";

// Manages the "my campaign — configure" page (member view). Three flows :
//   1. Add/remove recipients to the distribution → PATCH the distribution
//      with `feedback_campaign_recipients_attributes` (Rails accepts_nested
//      _attributes pattern). The `_destroy: true` flag removes a recipient.
//   2. Send feedback request to one recipient (or all pending) → POST to
//      send_requests with the recipient ids.
//   3. Send a reminder → POST to send_reminders.
//
// Page reloads after each successful mutation so server-rendered KPIs and
// recipients table stay accurate without DOM diffing.
export default class extends Controller {
  static targets = [
    "newEmail", "newName", "newRelation",
    "totalCount", "pendingCount",
    "recipientsBody",
    "message"
  ];

  static values = {
    distributionId:     String,
    updateUrl:          String,
    sendRequestsUrl:    String,
    sendRemindersUrl:   String,
    csrfToken:          String
  };

  // ── Recipients management ───────────────────────────────────────────

  async addRecipient(event) {
    event?.preventDefault?.();
    const email    = (this.newEmailTarget?.value || "").trim();
    const name     = (this.newNameTarget?.value || "").trim();
    const relation = (this.newRelationTarget?.value || "").trim();

    if (!email || !email.includes("@")) {
      this._showMessage("Email invalide.", "error");
      this.newEmailTarget?.focus();
      return;
    }

    this._showMessage("Ajout en cours…", "info");
    try {
      const res = await this._patchDistribution({
        feedback_campaign_recipients_attributes: [
          { recipient_email: email, recipient_name: name, relation_type: relation }
        ]
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err.errors || ["Ajout impossible"]).join(", "));
      }
      window.location.reload();
    } catch (e) {
      this._showMessage(e.message, "error");
    }
  }

  async removeRecipient(event) {
    event?.preventDefault?.();
    const recipientId = event.currentTarget.dataset.recipientId;
    if (!recipientId) return;
    if (!confirm("Retirer ce destinataire ? La demande envoyée éventuelle ne sera pas annulée.")) return;

    this._showMessage("Suppression…", "info");
    try {
      const res = await this._patchDistribution({
        feedback_campaign_recipients_attributes: [
          { id: recipientId, _destroy: true }
        ]
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err.errors || ["Suppression impossible"]).join(", "));
      }
      window.location.reload();
    } catch (e) {
      this._showMessage(e.message, "error");
    }
  }

  // ── Send requests ───────────────────────────────────────────────────

  async sendOne(event) {
    event?.preventDefault?.();
    const recipientId = event.currentTarget.dataset.recipientId;
    if (!recipientId) return;
    if (!confirm("Envoyer la demande de feedback à ce destinataire ?")) return;
    await this._sendTo([recipientId], false);
  }

  async sendAllPending(event) {
    event?.preventDefault?.();
    const ids = this._collectRecipientIds((row) => {
      // Find a row whose status pill is "En attente"
      const pill = row.querySelector('.jf-status, .jf-pill');
      const txt  = pill?.textContent?.trim().toLowerCase() || "";
      return txt === "en attente";
    });
    if (!ids.length) {
      this._showMessage("Aucun destinataire en attente.", "info");
      return;
    }
    if (!confirm(`Envoyer la demande à ${ids.length} destinataire${ids.length > 1 ? "s" : ""} en attente ?`)) return;
    await this._sendTo(ids, false);
  }

  async remindOne(event) {
    event?.preventDefault?.();
    const recipientId = event.currentTarget.dataset.recipientId;
    if (!recipientId) return;
    if (!confirm("Envoyer une relance à ce destinataire ?")) return;
    await this._sendTo([recipientId], true);
  }

  async sendAllReminders(event) {
    event?.preventDefault?.();
    const ids = this._collectRecipientIds((row) => {
      const pill = row.querySelector('.jf-status, .jf-pill');
      const txt  = pill?.textContent?.trim().toLowerCase() || "";
      // Sent or Viewed but not Responded
      return txt === "envoyé" || txt === "ouvert";
    });
    if (!ids.length) {
      this._showMessage("Aucun destinataire à relancer.", "info");
      return;
    }
    if (!confirm(`Envoyer une relance à ${ids.length} destinataire${ids.length > 1 ? "s" : ""} sans réponse ?`)) return;
    await this._sendTo(ids, true);
  }

  async _sendTo(recipientIds, isReminder) {
    const url = isReminder ? this.sendRemindersUrlValue : this.sendRequestsUrlValue;
    if (!url) return;
    this._showMessage(isReminder ? "Envoi des relances…" : "Envoi en cours…", "info");
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ recipient_ids: recipientIds })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Envoi impossible.");
      this._showMessage(data.message || "Envoyé.", "success");
      // Reload after a short delay so the user reads the success message
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      this._showMessage(e.message, "error");
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _collectRecipientIds(predicate) {
    if (!this.hasRecipientsBodyTarget) return [];
    return Array.from(this.recipientsBodyTarget.querySelectorAll("tr[data-recipient-id]"))
                .filter((row) => predicate(row))
                .map((row) => row.dataset.recipientId);
  }

  async _patchDistribution(distributionPayload) {
    return await fetch(this.updateUrlValue, {
      method: "PATCH",
      headers: this._jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({ feedback_campaign_distribution: distributionPayload })
    });
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
    };
  }

  _showMessage(text, tone) {
    if (!this.hasMessageTarget) return;
    const palette = {
      info:    ["var(--ds-color-info-500, #3b82f6)",   "var(--ds-color-info-50, #eff6ff)",    "var(--ds-color-info-700, #1d4ed8)"],
      success: ["var(--ds-color-success-500)",         "var(--ds-color-success-50, #ecfdf5)", "var(--ds-color-success-700)"],
      error:   ["var(--ds-color-danger-600)",          "rgba(220,38,38,0.08)",                "var(--ds-color-danger-700, #991b1b)"]
    };
    const [accent, bg, fg] = palette[tone] || palette.info;
    this.messageTarget.style.cssText = `
      display: flex; gap: 8px; align-items: center;
      padding: 8px 12px;
      border-radius: var(--ds-radius-md);
      background: ${bg}; color: ${fg};
      border-left: 3px solid ${accent};
      font-size: var(--ds-font-size-sm);
    `;
    this.messageTarget.textContent = text;
    this.messageTarget.hidden = false;
  }
}
